import React, { ReactNode, useCallback, useEffect, useMemo, useState } from "react";

import { faCheck } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { bem } from "@react-md/utils";
import { MenuProps } from "antd";
import { isEqual } from "lodash-es";

import RoleEnum from "@mapmycustomers/shared/enum/Role";
import Visibility from "@mapmycustomers/shared/enum/Visibility";
import Team from "@mapmycustomers/shared/types/Team";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";

import Button from "../Button";
import { useConfigProvider } from "../ConfigProvider";
import Dropdown from "../Dropdown";
import SearchField from "../SearchField";

const block = bem("mmc-visibility-selector");

export type VisibilityType =
  | Visibility.PRIVATE
  | Visibility.SHARED_WITH_ORGANIZATION
  | Visibility.SHARED_WITH_TEAM;

export interface VisibilitySelectorProps {
  allowedVisibilities: Visibility[];
  children: ReactNode;
  disabled?: boolean;
  getLabel?: (visibility: VisibilityType, defaultLabel: string) => string;
  onChange?: (visibility: VisibilityType, selectedTeams?: Team["id"][]) => void;
  selectedTeams?: Team["id"][];
  subLabel?: string;
  teams: Team[];
  userRole?: RoleEnum;
  value?: VisibilityType;
}

const VisibilitySelector = ({
  allowedVisibilities,
  children,
  disabled,
  getLabel,
  onChange,
  selectedTeams,
  subLabel,
  teams,
  userRole,
  value,
}: VisibilitySelectorProps) => {
  const configProvider = useConfigProvider();
  const [open, show, close] = useBoolean(false);
  const [visibility, setVisibility] = useState(value);
  const [selectedTeamIds, setSelectedTeamIds] = useState(selectedTeams);

  const [search, setSearch] = useState("");

  const isChanged = useMemo(
    () => visibility !== value || !isEqual(selectedTeamIds, selectedTeams),
    [selectedTeamIds, selectedTeams, value, visibility]
  );

  const handleOpenChange = useCallback(
    (open: boolean) => {
      (open ? show : close)();
      if (!open && visibility && isChanged) {
        onChange?.(visibility, selectedTeamIds);
      }
    },
    [close, isChanged, onChange, selectedTeamIds, show, visibility]
  );

  const handleSelect = useCallback(
    (info: { key: string }) => {
      setVisibility?.(info.key as VisibilityType);
      if (info.key !== Visibility.SHARED_WITH_TEAM) {
        onChange?.(info.key as VisibilityType);
        close();
        setSelectedTeamIds(undefined);
      }
    },
    [close, onChange]
  );

  const handleDone = useCallback(() => {
    if (visibility === Visibility.SHARED_WITH_TEAM) {
      onChange?.(visibility, selectedTeamIds);
    }
    close();
  }, [close, onChange, selectedTeamIds, visibility]);

  const filteredTeams = useMemo(
    () =>
      teams.filter(
        (team) => !search || team.name.trim().toLowerCase().includes(search.trim().toLowerCase())
      ),
    [search, teams]
  );

  const handleClick = useCallback(
    (teamId: Team["id"], selected: boolean) => {
      setVisibility(Visibility.SHARED_WITH_TEAM);
      setSelectedTeamIds(
        selected
          ? selectedTeamIds?.filter((id) => id !== teamId)
          : [...(selectedTeamIds ?? []), teamId]
      );
    },
    [selectedTeamIds]
  );

  const items: MenuProps["items"] = useMemo(() => {
    const items = [
      {
        key: Visibility.SHARED_WITH_ORGANIZATION,
        label: configProvider.formatMessage("ui.visibilitySelector.entireOrg"),
        value: Visibility.SHARED_WITH_ORGANIZATION,
      },
      {
        children: [
          {
            className: block("sub-menu"),
            disabled: true,
            key: "teams",
            label: (
              <div>
                <div className={block("teams-list")}>
                  <SearchField
                    onChange={setSearch}
                    placeholder={configProvider.formatMessage(
                      "ui.visibilitySelector.search.placeholder"
                    )}
                  />
                  <div className={block("teams")}>
                    {filteredTeams.map((team) => {
                      const selected = !!selectedTeamIds && selectedTeamIds.includes(team.id);
                      return (
                        <div
                          className={block("team-item", { selected })}
                          key={team.id}
                          onClick={() => handleClick(team.id, selected)}
                        >
                          {team.name}
                          {selected && (
                            <FontAwesomeIcon
                              className={block("checkMark")}
                              icon={faCheck}
                              size="lg"
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div className={block("done-button")}>
                  <Button onClick={handleDone} size="small" type="primary">
                    {configProvider.formatMessage("ui.visibilitySelector.done")}
                  </Button>
                </div>
              </div>
            ),
          },
        ],
        key: Visibility.SHARED_WITH_TEAM,
        label: configProvider.formatMessage(
          userRole !== RoleEnum.OWNER
            ? "ui.visibilitySelector.myTeam"
            : "ui.visibilitySelector.specificTeams"
        ),
        popupClassName: block("sub-menu-popup"),
        value: Visibility.SHARED_WITH_TEAM,
      },
      {
        key: Visibility.PRIVATE,
        label:
          getLabel?.(
            Visibility.PRIVATE,
            configProvider.formatMessage("ui.visibilitySelector.onlyMe")
          ) ?? configProvider.formatMessage("ui.visibilitySelector.onlyMe"),
        value: Visibility.PRIVATE,
      },
    ];
    return items.filter(({ value }) => allowedVisibilities.includes(value));
  }, [
    allowedVisibilities,
    configProvider,
    filteredTeams,
    handleClick,
    handleDone,
    selectedTeamIds,
    getLabel,
    userRole,
  ]);

  const menu: MenuProps = useMemo(() => {
    return {
      className: block("menu"),
      items,
      onSelect: handleSelect,
      openKeys: open ? undefined : [],
      selectable: true,
      selectedKeys: visibility ? [visibility] : [],
      triggerSubMenuAction: "click",
    };
  }, [items, handleSelect, open, visibility]);

  useEffect(() => {
    setVisibility(value);
  }, [value]);

  useEffect(() => {
    setSelectedTeamIds(selectedTeams);
  }, [selectedTeams]);

  return (
    <Dropdown
      className={block()}
      disabled={disabled}
      dropdownRender={(menu) => (
        <div className={block("dropdown-content")}>
          {subLabel && <span className={block("sub-label")}>{subLabel}</span>}
          {menu}
        </div>
      )}
      menu={menu}
      onOpenChange={handleOpenChange}
      open={open}
      trigger={["click"]}
    >
      {children}
    </Dropdown>
  );
};

export default VisibilitySelector;
